import { z } from 'zod';

/**
 * Exercise submission type enum schema
 */
export const exerciseSubmissionTypeSchema = z.enum([
  'MultipleChoice',
  'LongAnswer',
  'ShortAnswer',
  'Recorded',
  'FillInBlank',
  'SelectForBlank',
  'Conversational',
  'Matching',
  'Scramble',
  'FileUpload',
]);

export type ExerciseSubmissionType = z.infer<typeof exerciseSubmissionTypeSchema>;

// ============================================================================
// Multiple Choice Submission
// ============================================================================

const optionIndexArraySchema = z.preprocess(
  (value) => (Array.isArray(value) ? value : []),
  z.array(z.number().int().min(0))
);

const optionIdArraySchema = z.preprocess(
  (value) => (Array.isArray(value) ? value : []),
  z.array(z.string().min(1))
);

export const multipleChoiceSubmissionSchema = z
  .object({
    type: z.literal('MultipleChoice'),
    selectedOptionIndices: optionIndexArraySchema,
    selectedOptionIds: optionIdArraySchema,
  })
  .passthrough();

export type MultipleChoiceExerciseSubmission = z.infer<typeof multipleChoiceSubmissionSchema>;

// ============================================================================
// Written Submission (LongAnswer / ShortAnswer)
// ============================================================================

export const writtenSubmissionSchema = z.object({
  type: z.union([z.literal('LongAnswer'), z.literal('ShortAnswer')]),
  answer: z.string().default(''),
});

export type WrittenExerciseSubmission = z.infer<typeof writtenSubmissionSchema>;

// ============================================================================
// Recorded Submission
// ============================================================================

export const recordedSubmissionSchema = z.object({
  type: z.literal('Recorded'),
  recordingUrl: z.string().url().optional(),
  transcript: z.string().optional(),
});

export type RecordedExerciseSubmission = z.infer<typeof recordedSubmissionSchema>;

// ============================================================================
// Fill-in-Blank Submission
// ============================================================================

const fillInBlankAnswerSchema = z.union([z.string(), z.array(z.string())]);

export const fillInBlankSubmissionSchema = z.object({
  type: z.literal('FillInBlank'),
  answers: z.array(fillInBlankAnswerSchema).default([]),
});

export type FillInBlankExerciseSubmission = z.infer<typeof fillInBlankSubmissionSchema>;

// ============================================================================
// Select-for-Blank Submission
// ============================================================================

const selectionIndexSchema = z
  .union([z.number().int(), z.null()])
  .transform((value) => (value === undefined ? null : value));

export const selectForBlankSubmissionSchema = z.object({
  type: z.literal('SelectForBlank'),
  selections: z
    .array(z.array(selectionIndexSchema))
    .default([])
    .transform((sentences) => sentences.map((blanks) => blanks.map((value) => value ?? null))),
});

export type SelectForBlankExerciseSubmission = z.infer<typeof selectForBlankSubmissionSchema>;

// ============================================================================
// Conversational Submission
// ============================================================================

export const conversationalSubmissionSchema = z.object({
  type: z.literal('Conversational'),
  sessionId: z.string(),
  transcript: z.string().optional(),
});

export type ConversationalExerciseSubmission = z.infer<typeof conversationalSubmissionSchema>;

// ============================================================================
// Matching Submission
// ============================================================================

export const matchingSubmissionSchema = z.object({
  type: z.literal('Matching'),
  matches: z
    .array(
      z.object({
        promptId: z.string(),
        selectedOptionId: z.string(),
      })
    )
    .default([]),
});

export type MatchingExerciseSubmission = z.infer<typeof matchingSubmissionSchema>;

// ============================================================================
// Scramble Submission
// ============================================================================

export const scrambleSubmissionSchema = z.object({
  type: z.literal('Scramble'),
  // Array of word arrays per sentence - each sentence's words in the order the user arranged them
  answers: z.array(z.array(z.string())).default([]),
});

export type ScrambleExerciseSubmission = z.infer<typeof scrambleSubmissionSchema>;

// ============================================================================
// File Upload Submission
// ============================================================================

export const fileUploadSubmissionSchema = z.object({
  type: z.literal('FileUpload'),
  files: z.array(z.object({
    url: z.string().url(),
    filename: z.string().min(1),
    contentType: z.string().min(1),
    fileSize: z.number().positive(),
  })).min(1).max(5),
  comment: z.string().optional(),
});

export type FileUploadExerciseSubmission = z.infer<typeof fileUploadSubmissionSchema>;

// ============================================================================
// Combined Submission Schema
// ============================================================================

export const exerciseSubmissionSchema = z.union([
  multipleChoiceSubmissionSchema,
  writtenSubmissionSchema,
  recordedSubmissionSchema,
  fillInBlankSubmissionSchema,
  selectForBlankSubmissionSchema,
  conversationalSubmissionSchema,
  matchingSubmissionSchema,
  scrambleSubmissionSchema,
  fileUploadSubmissionSchema,
]);

export type ExerciseSubmissionPayload = z.infer<typeof exerciseSubmissionSchema>;
